import {ValueObject} from '@s-ui/domain'

import {InvalidParamsSharedError} from '../../shared/Errors/InvalidParamsSharedError.js'
import {QueryParamSharedValueObject} from '../../shared/ValueObjects/QueryParamSharedValueObject.js'
import {FilterVehicleSearchValueObject} from './FilterVehicleSearchValueObject.js'

export class FilterVehiclesSearchValueObject extends ValueObject {
  #vehicles

  constructor({vehicles}) {
    super()
    this.#vehicles = vehicles
  }

  static create({vehicles}) {
    return new FilterVehiclesSearchValueObject({vehicles: vehicles.map(FilterVehicleSearchValueObject.create)})
  }

  static createFromMakeIds({config, ids}) {
    if (!Array.isArray(ids) || !ids.length || ids.some(id => !id)) {
      throw InvalidParamsSharedError.create('[FilterVehiclesSearchValueObject] ids parameter must be a non-empty array')
    }
    return new FilterVehiclesSearchValueObject({
      vehicles: ids.map(id => FilterVehicleSearchValueObject.createWithDefaultValue({config, vehicle: {makeId: id}}))
    })
  }

  static createSingleVehicleFromMakeModel({config, makeId, modelId}) {
    if (!makeId) {
      throw InvalidParamsSharedError.create('[FilterVehiclesSearchValueObject] makeId must be a non-empty value')
    }
    return new FilterVehiclesSearchValueObject({
      vehicles: [
        FilterVehicleSearchValueObject.createWithDefaultValue({
          config,
          vehicle: {makeId, modelId}
        })
      ]
    })
  }

  static createWithDefaultValue({config, vehicles}) {
    const {DEFAULT_VEHICLES_SEARCH} = config.get('searchFilters')

    const currentVehicles = vehicles ?? DEFAULT_VEHICLES_SEARCH

    const cleanedVehicles = currentVehicles.filter(vehicle => vehicle.makeId || vehicle.modelId || vehicle.version)

    return new FilterVehiclesSearchValueObject({
      vehicles: cleanedVehicles.map(vehicle => FilterVehicleSearchValueObject.createWithDefaultValue({config, vehicle}))
    })
  }

  static createFromGroupedModel({vehicles = []}) {
    return new FilterVehiclesSearchValueObject({
      vehicles: vehicles.map(FilterVehicleSearchValueObject.createFromGroupedModel)
    })
  }

  static createQueryParams({config, vehicles}) {
    const APP_NAME_COCHES = config.get('APP_NAME_COCHES')
    const APP_NAME = config.get('APP_NAME')
    const {DEFAULT_VEHICLE_SEARCH_VALUE, FILTER_NAMES, FILTER_QUERY_PARAMS} = config.get('searchFilters')

    const isCoches = APP_NAME === APP_NAME_COCHES

    return new FilterVehiclesSearchValueObject({
      vehicles: {
        makes: QueryParamSharedValueObject.createForSingle({
          key: FILTER_QUERY_PARAMS[FILTER_NAMES.VEHICLES].makeId,
          value: vehicles?.map(({makeId}) => makeId || DEFAULT_VEHICLE_SEARCH_VALUE.makeId)
        }),
        models: QueryParamSharedValueObject.createForSingle({
          key: isCoches
            ? FILTER_QUERY_PARAMS[FILTER_NAMES.VEHICLES].modelId
            : FILTER_QUERY_PARAMS[FILTER_NAMES.VEHICLES].modelName,
          value: vehicles?.map(({model = {}}) =>
            isCoches
              ? model.id ?? DEFAULT_VEHICLE_SEARCH_VALUE.modelId
              : model.name || DEFAULT_VEHICLE_SEARCH_VALUE.model
          )
        }),
        versions: QueryParamSharedValueObject.createForSingle({
          key: FILTER_QUERY_PARAMS[FILTER_NAMES.VEHICLES].version,
          value: vehicles?.map(({version}) => version || DEFAULT_VEHICLE_SEARCH_VALUE.version)
        })
      }
    })
  }

  get isEmpty() {
    return !this.#vehicles.length
  }

  get queryParams() {
    return {
      ...(!this.#vehicles.makes.isEmpty && {...this.#vehicles.makes.queryParam}),
      ...(!this.#vehicles.models.isEmpty && {...this.#vehicles.models.queryParam}),
      ...(!this.#vehicles.versions.isEmpty && {...this.#vehicles.versions.queryParam})
    }
  }

  get valueWithGroupedModel() {
    return this.#vehicles.map(vehicle => vehicle.valueWithGroupedModel)
  }

  get value() {
    return this.#vehicles.map(vehicle => vehicle.value)
  }

  getValue() {
    return {vehicles: this.value}
  }

  toJSON() {
    return {vehicles: this.#vehicles.map(vehicle => vehicle.toJSON())}
  }
}
